import { useEffect } from "react";

/**
 * Custom hook to dynamically manage document head meta tags for SEO and Social Media sharing.
 *
 * @param {Object} props
 * @param {string} props.title - The document title
 * @param {string} props.description - The page description meta tag
 * @param {string} props.keywords - Commas separated list of keywords
 * @param {string} [props.ogTitle] - Open Graph sharing title (defaults to title)
 * @param {string} [props.ogDescription] - Open Graph sharing description (defaults to description)
 * @param {string} [props.ogImage] - Open Graph sharing image URL
 * @param {string} [props.ogUrl] - Open Graph page URL (defaults to current window.location.href)
 * @param {string} [props.twitterCard="summary_large_image"] - Twitter Card layout style
 * @param {string} [props.twitterTitle] - Twitter sharing title (defaults to title)
 * @param {string} [props.twitterDescription] - Twitter sharing description (defaults to description)
 * @param {string} [props.twitterImage] - Twitter sharing image URL (defaults to ogImage)
 */
const useMetaTags = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
}) => {
  useEffect(() => {
    // 1. Dynamic Title
    if (title) {
      document.title = title.includes("Abilio Realty")
        ? title
        : `${title} | Abilio Realty`;
    }

    // Helper to update an existing tag or create a new one inside head
    const updateOrCreateMeta = (attrName, attrValue, content) => {
      if (!content) return;
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // 2. Standard Meta Tags
    if (description) {
      updateOrCreateMeta("name", "description", description);
    }
    if (keywords) {
      updateOrCreateMeta("name", "keywords", keywords);
    }

    // 3. Open Graph (Facebook / LinkedIn) Meta Tags
    updateOrCreateMeta("property", "og:title", ogTitle || title);
    updateOrCreateMeta(
      "property",
      "og:description",
      ogDescription || description,
    );
    if (ogImage) {
      updateOrCreateMeta("property", "og:image", ogImage);
    }
    updateOrCreateMeta("property", "og:url", ogUrl || window.location.href);
    updateOrCreateMeta("property", "og:type", "website");

    // 4. Twitter Card Meta Tags
    updateOrCreateMeta("name", "twitter:card", twitterCard);
    updateOrCreateMeta(
      "name",
      "twitter:title",
      twitterTitle || ogTitle || title,
    );
    updateOrCreateMeta(
      "name",
      "twitter:description",
      twitterDescription || ogDescription || description,
    );
    if (twitterImage || ogImage) {
      updateOrCreateMeta("name", "twitter:image", twitterImage || ogImage);
    }
  }, [
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
  ]);
};

export default useMetaTags;
